import { ButtonHTMLAttributes, FC } from 'react'
import convert from 'xml-js'
import { ButtonStyle } from 'ts/types'
import {
  GradientStop,
  LinearGradientBrush
} from 'ts/types/ButtonStyle'

interface Props {
  data: ButtonStyle
  className?: string
}

const ButtonStyleItem: FC<Props & ButtonHTMLAttributes<HTMLButtonElement>> = ({
  data,
  className,
  ...rest
}) => {
  // Helper function to safely parse color values (XML or hex)
  const parseColor = (colorValue: string | null): string => {
    if (!colorValue) return "FFFFFF"
    
    // Check if it's a simple hex color (starts with #)
    if (colorValue.startsWith('#')) {
      return colorValue.substring(1) // Remove the # prefix
    }
    
    // Try to parse as XML
    try {
      const xmlResult = convert.xml2js(colorValue)
      if (
        xmlResult &&
        xmlResult.elements &&
        xmlResult.elements[0] &&
        xmlResult.elements[0].elements &&
        xmlResult.elements[0].elements[0] &&
        xmlResult.elements[0].elements[0].text
      ) {
        return xmlResult.elements[0].elements[0].text.substring(3)
      }
    } catch (error) {
      console.warn('Failed to parse color as XML:', colorValue, error)
    }
    
    return "FFFFFF" // Default fallback
  }

  const foreground = data.foreground ? convert.xml2js(data.foreground) : null
  const borderstyle = data.borderstyle ? convert.xml2js(data.borderstyle) : null
  const text = data.text ? convert.xml2js(data.text) : null

  // Parse colors using the helper function
  const foregroundInner = parseColor(data.foreground)
  const borderInner = parseColor(data.borderstyle)
  const textInner = parseColor(data.text)

  let style = {}
  let foregroundName = "SolidColorBrush"
  let gradientFrom
  let gradientTo

  // Determine if it's a gradient brush by checking the XML structure
  if (
    foreground &&
    foreground.elements &&
    foreground.elements[0] &&
    foreground.elements[0].name
  ) {
    foregroundName = foreground.elements[0].name
  }

  switch (foregroundName) {
    case "SolidColorBrush":
      style = {
        backgroundColor: `#${foregroundInner}`
      }
      break
    case "LinearGradientBrush":
      if (foreground && foreground.elements && foreground.elements[0]) {
        try {
          gradientFrom = foreground.elements[0].elements?.[0]?.elements?.[0]?.attributes?.Color?.substring(3)
          gradientTo = foreground.elements[0].elements?.[0]?.elements?.[1]?.attributes?.Color?.substring(3)
          
          style = {
            backgroundImage: `linear-gradient(#${gradientFrom || "FFFFFF"}, #${gradientTo || "FFFFFF"})`
          }
        } catch (error) {
          console.warn('Failed to parse gradient colors:', error)
          style = {
            backgroundColor: `#${foregroundInner}`
          }
        }
      } else {
        style = {
          backgroundColor: `#${foregroundInner}`
        }
      }
      break

    default:
      style = {
        backgroundColor: `#${foregroundInner}`
      }
      break
  }

  style = {
    ...style,
    color: `#${textInner}`,
    borderStyle: 'solid',
    borderWidth: '2px',
    borderColor: `#${borderInner}`
  }

  return (
    <button
      type="button"
      style={style}
      className={`flex items-center justify-center w-24 h-16 p-2 text-xs text-center rounded-lg shadow focus:outline-none ${className}`}
      {...rest}
    >
      {data.name}
    </button>
  )
}

export default ButtonStyleItem
